import { api } from '@features';

import {
  IAutoScalePlan,
  IAutoScaleRegion,
  IAutoScaleRegionSetting,
  IAutoScaleResolvedPlan,
  IAutoScaleSchedule,
  IAutoScaleStatus,
} from './AutoScale';

export const autoScaleApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAutoScaleRegions: builder.query<IAutoScaleRegion[], void>({
      query: () => ({ method: 'GET', url: `/autoscale/regions` }),
      extraOptions: { maxRetries: 0 },
      providesTags: ['AutoScale'],
      transformResponse: (response: { regions: IAutoScaleRegion[]; success: boolean }) => {
        if (response.success) {
          return response.regions;
        }
        return Promise.reject('Unauthorized');
      },
    }),

    getAutoScaleStatus: builder.query<IAutoScaleStatus, void>({
      query: () => ({ method: 'GET', url: `/autoscale/status` }),
      extraOptions: { maxRetries: 0 },
      providesTags: ['AutoScale'],
      transformResponse: (response: { status: IAutoScaleStatus; success: boolean }) => {
        if (response.success) {
          return response.status;
        }
        return Promise.reject('Unauthorized');
      },
    }),

    getAutoScalePlans: builder.query<IAutoScalePlan[], void>({
      query: () => ({ method: 'GET', url: `/autoscale/plans` }),
      extraOptions: { maxRetries: 0 },
      providesTags: ['AutoScale'],
      transformResponse: (response: { plans: IAutoScalePlan[]; success: boolean }) => {
        if (response.success) {
          return response.plans;
        }
        return Promise.reject('Unauthorized');
      },
    }),

    getAutoScaleSettings: builder.query<
      { overrides: IAutoScaleRegionSetting[]; resolved: IAutoScaleResolvedPlan[] },
      void
    >({
      query: () => ({ method: 'GET', url: `/autoscale/settings` }),
      extraOptions: { maxRetries: 0 },
      providesTags: ['AutoScale'],
      transformResponse: (response: {
        overrides: IAutoScaleRegionSetting[];
        resolved: IAutoScaleResolvedPlan[];
        success: boolean;
      }) => {
        if (response.success) {
          return { overrides: response.overrides, resolved: response.resolved };
        }
        return Promise.reject('Unauthorized');
      },
    }),

    setAutoScaleRegionPlan: builder.mutation<
      IAutoScaleRegionSetting,
      { regionId: string; role: string; plan: string }
    >({
      query: (body) => ({ method: 'PUT', url: `/autoscale/settings`, body }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['AutoScale'],
      transformResponse: (response: { setting: IAutoScaleRegionSetting }) => response.setting,
      transformErrorResponse: (response: any) => response.data,
    }),

    deleteAutoScaleRegionPlan: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ method: 'DELETE', url: `/autoscale/settings/${id}` }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['AutoScale'],
    }),

    getAutoScaleSchedules: builder.query<IAutoScaleSchedule[], void>({
      query: () => ({ method: 'GET', url: `/autoscale/schedule` }),
      extraOptions: { maxRetries: 0 },
      providesTags: ['AutoScale'],
      transformResponse: (response: { schedules: IAutoScaleSchedule[]; success: boolean }) => {
        if (response.success) {
          return response.schedules;
        }
        return Promise.reject('Unauthorized');
      },
    }),

    createAutoScaleSchedule: builder.mutation<IAutoScaleSchedule, Partial<IAutoScaleSchedule>>({
      query: (schedule) => ({ method: 'POST', url: `/autoscale/schedule`, body: schedule }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['AutoScale'],
      transformResponse: (response: { schedule: IAutoScaleSchedule; success: boolean }) => response.schedule,
      transformErrorResponse: (response: any) => response.data,
    }),

    updateAutoScaleSchedule: builder.mutation<
      IAutoScaleSchedule,
      Partial<IAutoScaleSchedule> & { _id: string }
    >({
      query: ({ _id, ...patch }) => ({ method: 'PUT', url: `/autoscale/schedule/${_id}`, body: patch }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['AutoScale'],
      transformResponse: (response: { schedule: IAutoScaleSchedule; success: boolean }) => response.schedule,
      transformErrorResponse: (response: any) => response.data,
    }),

    deleteAutoScaleSchedule: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ method: 'DELETE', url: `/autoscale/schedule/${id}` }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: ['AutoScale'],
    }),
  }),
});

export const {
  useGetAutoScaleRegionsQuery,
  useGetAutoScalePlansQuery,
  useGetAutoScaleSettingsQuery,
  useSetAutoScaleRegionPlanMutation,
  useDeleteAutoScaleRegionPlanMutation,
  useGetAutoScaleStatusQuery,
  useGetAutoScaleSchedulesQuery,
  useCreateAutoScaleScheduleMutation,
  useUpdateAutoScaleScheduleMutation,
  useDeleteAutoScaleScheduleMutation,
} = autoScaleApi;
